import { LetsGetYouBookedMacroComponent } from "./Macros/LetsGetYouBookedMacroComponent";
import { BookingConfirmationMacroComponent } from "./Macros/BookingConfirmationMacroComponent";
import { LetsGetYouBookedMacroPaymentLinkComponent } from "./Macros/LetsGetYouBookedMacroPaymentLinkComponent";
import { PaymentLinkOnlyComponent } from "./Macros/PaymentLinkOnlyComponent";

// Puck template-based macro components (for comparison with hardcoded versions)
import { PuckBookingConfirmationMacroComponent } from "./Macros/PuckBookingConfirmationMacroComponent";
import { PuckLetsGetYouBookedMacroComponent } from "./Macros/PuckLetsGetYouBookedMacroComponent";
import { PuckLetsGetYouBookedMacroPaymentLinkComponent } from "./Macros/PuckLetsGetYouBookedMacroPaymentLinkComponent";

import type { ReservationState } from '@digitalopsdev/serviceops-reservations-components/dist/EditReservation/types';

import { registerReservationLogEntryDeserializer } from '@digitalopsdev/serviceops-reservations-components/dist/EditReservation/ReservationLogEntry';
import { registerReservationLogEntryRenderer } from '@digitalopsdev/serviceops-reservations-components/dist/EditReservation/ReservationLogsRenderer';
import React from 'react';
import EmailSnapshotLogEntry from './Renderers/EmailSnapshotLogEntry';
import { GuestVouchersToolButton } from '../GuestVouchers/GuestVouchersToolButton';
import { SnapshotsToolButton } from '../RateSnapshots/SnapshotsToolButton';
import { PrintBookingConfirmationToolButton } from '../PrintBookingConfirmation/PrintBookingConfirmationToolButton';
import { BookingEngineToolButton } from '../BookingEngineTool/BookingEngineToolButton';

let emailSnapshotRegistered = false;


function registerEmailSnapshotRenderers() {
    if (emailSnapshotRegistered) return;
    emailSnapshotRegistered = true;

    // Deserializer maps DTO {$type:'EmailSnapshot', entries:[...]} to a domain object with $kind for serializer/renderer lookup
    registerReservationLogEntryDeserializer('EmailSnapshot', (dto: any) => {
        const d: any = dto ?? {};
        const entries = Array.isArray(d.entries) ? d.entries : [];
        const e: any = { $kind: 'EmailSnapshot', [Symbol.for('ReservationLogEntry')]: true, Entries: entries };
        return e;
    });

    // Renderer: mount the React component
    registerReservationLogEntryRenderer('EmailSnapshot', (entry: any) => {
        return React.createElement(EmailSnapshotLogEntry, { entry });
    });
}

// Public, idempotent pre-registration so Razor pages can register BEFORE model load
export function registerReservationLogPlugins() {
    registerEmailSnapshotRenderers();
}

export function onModelLoaded(reservationState: ReservationState) {
    reservationState.ProgramTypes = {
        'WeightLoss': {
            Key: 'WeightLoss',
            Name: 'Weight Loss Program',
        },
        'TotalFitness': {
            Key: 'TotalFitness',
            Name: 'Total Fitness Program',
        },
        'ShortStay': {
            Key: 'ShortStay',
            Name: 'Short Stay Program',
        },
        'MealsOnly': {
            Key: 'MealsOnly',
            Name: 'Meals Only',
        }
    };

    reservationState.MacroConfigurations = [
        {
            key: 'LetsGetYouBooked',
            displayName: "Let's get you booked",
            component: LetsGetYouBookedMacroComponent
        },
        {
            key: 'BookingConfirmation',
            displayName: "Booking confirmation",
            component: BookingConfirmationMacroComponent
        },
        {
            key: 'LetsGetYouBookedPaymentLink',
            displayName: "Let's Get You Booked - Payment Link",
            component: LetsGetYouBookedMacroPaymentLinkComponent
        },
        {
            key: 'PaymentLinkOnly',
            displayName: 'Payment Link Only',
            component: PaymentLinkOnlyComponent
        },
        // Puck template-based macros (for testing/comparison)
        {
            key: 'PuckLetsGetYouBooked',
            displayName: "[Test] Let's get you booked",
            component: PuckLetsGetYouBookedMacroComponent
        },
        {
            key: 'PuckLetsGetYouBookedPaymentLink',
            displayName: "[Test] Let's Get You Booked - Payment Link",
            component: PuckLetsGetYouBookedMacroPaymentLinkComponent
        },
        {
            key: 'PuckBookingConfirmation',
            displayName: "[Test] Booking confirmation",
            component: PuckBookingConfirmationMacroComponent
        }
    ];

    // Inject tool buttons into the Tools card.
    reservationState.renderToolsButtons = () => {
        if (!reservationState.ReservationId) return null;
        return React.createElement(React.Fragment, null,
            React.createElement(GuestVouchersToolButton, {
                reservationId: reservationState.ReservationId,
            }),
            React.createElement(SnapshotsToolButton, {
                reservationState,
            }),
            React.createElement(PrintBookingConfirmationToolButton, {
                reservationId: reservationState.ReservationId,
            }),
            React.createElement(BookingEngineToolButton, {
                reservationState,
            }),
        );
    };
}
